import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Filter, Search, LogIn, AlertTriangle, Database, RefreshCw, ArrowUp, ArrowDown, BarChart } from 'lucide-react';
import { GameCard } from '../components/GameCard';
import {
  toggleOwned,
  toggleWishlist,
  togglePlayed,
  removeFromCollection,
  updatePlayCount,
  getPagedUserCollection,
  CollectionFilters,
  Pagination,
  UserGame,
} from '../lib/game/collectionService';
import { rateGame } from '../lib/game/ratingService';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import SEO from '../components/SEO';

const PAGE_SIZE = 20;

// ATUALIZADO: Definir o tipo para sortBy para ser o mesmo que em collectionService.ts
// Idealmente, importaríamos CollectionSort de collectionService e usaríamos CollectionSort['sortBy']
// mas para manter a alteração focada, vamos atualizar o tipo local.
type SortByType = 'game_name' | 'ug_rating' | 'ug_updated_at' | 'g_average_rating' | 'g_complexity' | 'g_year';

// Manter a interface CollectionSort local, mas garantindo que sortBy use o SortByType atualizado.
interface CollectionSort {
  sortBy: SortByType; 
  ascending: boolean;
}

const CollectionPage: React.FC = () => {
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const [pagedCollection, setPagedCollection] = useState<UserGame[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<CollectionFilters['type']>('owned');
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState<CollectionSort>({ sortBy: 'game_name', ascending: true });
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearchText = useDebounce(searchText, 500);

  const navigate = useNavigate();

  const loadPagedCollection = useCallback(async () => {
    if (!user?.id) {
      setLoading(false); // Garantir que o loading termine se não houver usuário
      return;
    }

    setLoading(true);
    setError(null);
    setDiagnosticInfo(null); // Limpar diagnósticos antigos
    console.log("[COLLECTION_PAGE] Carregando coleção paginada via RPC...", { userId: user.id, filterType, debouncedSearchText, sortConfig, currentPage });

    try {
      const filters: CollectionFilters = {
        type: filterType,
        searchText: debouncedSearchText || null, // Passar null se vazio
      };
      const pagination: Pagination = {
        page: currentPage,
        pageSize: PAGE_SIZE,
      };
      
      // Passar sortConfig diretamente, pois já tem o tipo correto
      const result = await getPagedUserCollection(user.id, filters, sortConfig, pagination);
      
      console.log("[COLLECTION_PAGE] Resultado recebido da RPC:", result);

      if (result && result.data) {
        setPagedCollection(result.data);
        setTotalCount(result.count ?? 0); // Usar ?? 0 para garantir um número
        
        if (isAdmin) {
          setDiagnosticInfo(`Coleção carregada via RPC. UserId: ${user.id}, Page: ${currentPage}, Filters: ${JSON.stringify(filters)}, Sort: ${JSON.stringify(sortConfig)}, Total: ${result.count}, Encontrados: ${result.data.length}`); // Usar sortConfig
        }
      } else {
         // Se result for null ou data for null/undefined, tratar como vazio
         setPagedCollection([]);
         setTotalCount(0);
         if (isAdmin) {
           setDiagnosticInfo(`RPC retornou resultado vazio ou inválido. UserId: ${user.id}, Filters: ${JSON.stringify(filters)}, Sort: ${JSON.stringify(sortConfig)}`); // Usar sortConfig
         }
      }

    } catch (error) {
      console.error('[COLLECTION_PAGE] Erro ao carregar coleção via RPC:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      setError(`Não foi possível carregar sua coleção: ${errorMessage}`);
      if (isAdmin) {
        setDiagnosticInfo(`Erro ao carregar via RPC: ${errorMessage}`);
      }
      setPagedCollection([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filterType, debouncedSearchText, sortConfig, currentPage, isAdmin]); // Manter as dependências corretas

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      // Quando o componente é montado ou quando as dependências mudam, force uma atualização completa
      // ignorando o cache para garantir dados atualizados
      const forceReload = async () => {
        console.log("[COLLECTION_PAGE] Forçando atualização completa da coleção...");
        // Limpar o estado atual
        setPagedCollection([]);
        setTotalCount(0);
        // Iniciar o carregamento
        await loadPagedCollection();
      }
      
      forceReload();
    } else if (!authLoading && !isAuthenticated) {
      console.log("[COLLECTION_PAGE] Usuário não autenticado");
      setPagedCollection([]);
      setTotalCount(0);
      setCurrentPage(1);
      setLoading(false);
    }
  }, [user, isAuthenticated, authLoading, filterType, debouncedSearchText, sortConfig, currentPage, loadPagedCollection]);

  const handleFilterChange = (newFilter: CollectionFilters['type']) => {
    setFilterType(newFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1); // Reset page on new search
  };

  // Ajustar handleSortChange para usar o tipo correto e mapear se necessário
  const handleSortChange = (newSortByValue: string) => {
    // AGORA: newSortByValue deve ser um dos valores de SortByType vindo do <select>
    // Apenas fazemos um cast, assumindo que o <select> só envia valores válidos.
    const mappedSortBy = newSortByValue as SortByType;

    setSortConfig(prevSort => {
      const isAscending = prevSort.sortBy === mappedSortBy ? !prevSort.ascending : true;
      return { sortBy: mappedSortBy, ascending: isAscending };
    });
    setCurrentPage(1); // Reset page on sort change
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user) return;
    try {
      await toggleOwned(user.id, gameId, isOwned);
      loadPagedCollection();
    } catch (error) {
      console.error('Erro ao atualizar propriedade owned:', error);
      setError('Não foi possível atualizar o jogo. Tente novamente.');
    }
  };

  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user) return;
    try {
      await toggleWishlist(user.id, gameId, isWishlist);
      loadPagedCollection();
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
      setError('Não foi possível atualizar o jogo. Tente novamente.');
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user) return;
    
    try {
      setError(null); // Limpar qualquer erro anterior
      console.log(`[CollectionPage] Tentando marcar jogo ${gameId} como ${isPlayed ? 'jogado' : 'não jogado'}`);
      
      const updatedUserGame = await togglePlayed(user.id, gameId, isPlayed);
      console.log('[CollectionPage] Jogo atualizado com sucesso:', updatedUserGame);
      
      // Atualizar a UI com os dados atualizados
      await loadPagedCollection();
    } catch (error) {
      console.error('[CollectionPage] Erro ao marcar jogo como jogado:', error);
      setError('Não foi possível atualizar o status de jogado. Tente novamente.');
      
      // Mostrar alerta para o usuário em caso de erro
      window.alert('Não foi possível marcar o jogo como jogado. Tente novamente.');
    }
  };

  const handleRateGame = async (gameId: string, rating: number) => {
    if (!user) return;
    try {
      await rateGame(user.id, gameId, rating);
      loadPagedCollection();
    } catch (error) {
      console.error('Erro ao avaliar jogo:', error);
      setError('Não foi possível avaliar o jogo. Tente novamente.');
    }
  };

  const handleRemoveGame = async (gameId: string) => {
    if (!user) return;
    if (window.confirm("Tem certeza que deseja remover este jogo da sua coleção?")) {
      try {
        await removeFromCollection(user.id, gameId);
        loadPagedCollection();
      } catch (error) {
        console.error('Erro ao remover jogo:', error);
        setError('Não foi possível remover o jogo. Tente novamente.');
      }
    }
  };

  const handleUpdatePlayCount = async (gameId: string, playCount: number) => {
    if (!user) return;
    try {
      navigate(`/collection/played?gameId=${gameId}&action=register`);
    } catch (error) {
      console.error('Erro ao navegar para registro de partida:', error);
      setError('Não foi possível acessar o formulário de registro de partida. Tente novamente.');
    }
  };

  const renderDiagnostic = () => {
    if (!isAdmin) return null;
    
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/70 border border-yellow-200 dark:border-yellow-700 p-4 rounded-lg mb-6">
        <h3 className="font-semibold flex items-center mb-2 text-yellow-800 dark:text-yellow-300">
          <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600 dark:text-yellow-400" />
          Diagnóstico do Sistema (Admin)
        </h3>
        
        {diagnosticInfo && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-1 text-yellow-700 dark:text-yellow-200">Informações de Diagnóstico:</h4>
            <div className="text-xs font-mono overflow-x-auto p-2 bg-black dark:bg-gray-800 text-green-400 dark:text-green-300 rounded">
              {diagnosticInfo}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Você precisa estar conectado para ver sua coleção
          </h3>
          <p className="text-gray-600 mb-6">
            Entre na sua conta para acessar e gerenciar seus jogos
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out"
          >
            <LogIn className="h-5 w-5 mr-2" />
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const sortOptions = [
    { value: 'game_name', label: 'Nome' },
    { value: 'ug_rating', label: 'Minha Nota' },
    { value: 'g_average_rating', label: 'Nota Média (Jogo)' },
    { value: 'g_complexity', label: 'Complexidade (Jogo)' },
    { value: 'g_year', label: 'Ano Lançamento (Jogo)' },
    { value: 'ug_updated_at', label: 'Atualizado/Adicionado em' },
  ];

  return (
    <>
      <SEO title="Minha Coleção" description="Gerencie e explore sua coleção de jogos de tabuleiro." />
      <div className="container mx-auto px-2 sm:px-4 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100">Minha Coleção</h1>
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <Link 
                to="/collection/stats"
                className="flex items-center px-4 py-2 bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors text-sm"
              >
                <BarChart className="h-5 w-5 mr-2" />
                Ver Estatísticas
              </Link>
            )}
            {isAuthenticated && (
              <Link 
                to="/add-game" 
                className="flex items-center px-4 py-2 bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white font-medium rounded-md shadow-sm transition-colors text-sm"
              >
                <PlusCircle className="h-5 w-5 mr-2" />
                Adicionar Novo Jogo
              </Link>
            )}
          </div>
        </div>

        {renderDiagnostic()}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/70 border-l-4 border-red-400 dark:border-red-600 p-4 mb-6 rounded-md shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-500" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <label htmlFor="filterType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mostrar:</label>
              <div className="flex rounded-md shadow-sm">
                {(user ? ['owned', 'wishlist', 'played'] : []).map(type => (
                  <button
                    key={type}
                    onClick={() => handleFilterChange(type as CollectionFilters['type'])}
                    className={`relative inline-flex items-center justify-center px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 first:rounded-l-md last:rounded-r-md focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors
                      ${filterType === type 
                        ? 'bg-indigo-600 dark:bg-indigo-500 text-white' 
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}
                    `}
                  >
                    {type === 'owned' && 'Possuídos'}
                    {type === 'wishlist' && 'Wishlist'}
                    {type === 'played' && 'Jogados'}
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Buscar na coleção:</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                </div>
                <input
                  type="search"
                  name="search"
                  id="search"
                  value={searchText}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 sm:text-sm shadow-sm"
                  placeholder="Nome do jogo..."
                />
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-1">
              <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ordenar por:</label>
              <div className="relative">
                <select
                  id="sortBy"
                  name="sortBy"
                  value={sortConfig.sortBy} // Usar sortConfig.sortBy
                  onChange={(e) => handleSortChange(e.target.value)} // Passar e.target.value
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                >
                  <option value="game_name">Nome (A-Z)</option>
                  <option value="ug_rating">Meu Rating (Maior)</option>
                  <option value="g_average_rating">Nota Média (Jogo)</option>
                  <option value="g_complexity">Complexidade (Jogo)</option>
                  <option value="g_year">Ano Lançamento (Jogo)</option>
                  <option value="ug_updated_at">Atualizado/Adicionado em</option>
                </select>
                <button 
                  onClick={() => setSortConfig(prev => ({ ...prev, ascending: !prev.ascending }))}
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  aria-label={sortConfig.ascending ? "Ordenar Descendente" : "Ordenar Ascendente"}
                >
                  {sortConfig.ascending ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando sua coleção...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-lg text-red-700 mb-4">
            <p>{error}</p>
          </div>
        ) : pagedCollection.length > 0 ? (
          <>
            <div className="gameCard__container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pagedCollection.map(userGame => {
                // Log para inspecionar os dados antes de passar para GameCard
                console.log('[CollectionPage] Rendering GameCard for userGame:', userGame);
                return (
                  <GameCard
                    key={userGame.id}
                    game={userGame.game}
                    userGame={userGame}
                    onToggleOwned={handleToggleOwned}
                    onToggleWishlist={handleToggleWishlist}
                    onTogglePlayed={handleTogglePlayed}
                    onRemove={handleRemoveGame}
                    onRate={handleRateGame}
                    onUpdatePlayCount={handleUpdatePlayCount}
                    isInCollection={true}
                    isInitiallyOwned={filterType === 'owned' || !!userGame.owned}
                    isInitiallyWishlist={filterType === 'wishlist' || !!userGame.wishlist}
                    isInitiallyPlayed={(filterType === 'played' || !!userGame.play_count && userGame.play_count > 0)}
                    user={user}
                  />
                );
              })}
            </div>

            {totalPages > 1 && !debouncedSearchText && (
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm border border-gray-300 dark:border-gray-600 transition-colors
                    ${currentPage === 1 || loading 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                      : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500'}`}
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Página {currentPage} de {Math.ceil(totalCount / PAGE_SIZE)}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE) || loading}
                  className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm border border-gray-300 dark:border-gray-600 transition-colors
                    ${currentPage >= Math.ceil(totalCount / PAGE_SIZE) || loading 
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                      : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500'}`}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-800 mb-4">
              <Database className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Sua coleção de '{filterType === 'owned' ? 'possuídos' : filterType === 'wishlist' ? 'lista de desejos' : 'jogados'}' está vazia
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filterType === 'owned' && "Comece adicionando jogos que você possui!"}
              {filterType === 'wishlist' && "Adicione jogos à sua lista de desejos para acompanhá-los."}
              {filterType === 'played' && "Registre as partidas dos seus jogos favoritos."}
            </p>
            <div className="mt-6">
              <Link
                to="/search"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Buscar Jogos para Adicionar
              </Link>
            </div>
          </div>
        )}

        {isAuthenticated && totalCount > 0 && (
          <div className="mt-6 mb-2 text-sm text-gray-600 dark:text-gray-400">
            Mostrando {pagedCollection.length} de {totalCount} resultados.
          </div>
        )}
      </div>
    </>
  );
};

export default CollectionPage; 